import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

import { NgbModule, NgbScrollSpyModule } from '@ng-bootstrap/ng-bootstrap';
import { SanitizeHtmlPipe } from './pipes/sanitize.pipe';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbdListSortableHeader } from './directives/listjs-sortable.directive';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';

import { defineElement } from 'lord-icon-element';
import lottie from 'lottie-web';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FlatpickrModule } from 'angularx-flatpickr';
import { DndDirective } from './directives/dnd.directive';
import { FilePondModule } from 'ngx-filepond';
import { EulaAgreementComponent } from './components/eula-agreement/eula-agreement.component';
import { CreateUserComponent } from './components/create-user/create-user.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { AssignProjectMemberComponent } from './components/assign-project-member/assign-project-member.component';
import { CreateProjectComponent } from './components/create-project/create-project.component';
import { DateFormatterPipe } from './pipes/date-formatter.pipe';
import { QuestionnaireChaptersComponent } from './components/questionnaire-chapters/questionnaire-chapters.component';
import { SimplebarAngularModule } from 'simplebar-angular';
import { ChapterQuestionsComponent } from './components/questionnaire-chapters/chapter-questions/chapter-questions.component';
import { ViewChapterQuestionComponent } from './components/questionnaire-chapters/view-chapter-question/view-chapter-question.component';
import { InviteExamineeComponent } from './components/invite-examinee/invite-examinee.component';
import { TruncatePipe } from './pipes/truncate.pipe';
import { CreateConversationComponent } from './components/create-conversation/create-conversation.component';
import { UploadConversationVideoComponent } from './components/upload-conversation-video/upload-conversation-video.component';
import { ConversationDataComponent } from './components/conversation-data/conversation-data.component';
import { ConvEqualizerChartComponent } from './components/conversation-data/conv-equalizer-chart/conv-equalizer-chart.component';
import { ConvPolarChartComponent } from './components/conversation-data/conv-polar-chart/conv-polar-chart.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { PlotlyModule } from 'angular-plotly.js';
import * as PlotlyJS from 'plotly.js-dist-min';
import { ConvHeatmapChartComponent } from './components/conversation-data/conv-heatmap-chart/conv-heatmap-chart.component';
import { ConvVoiceChartComponent } from './components/conversation-data/conv-voice-chart/conv-voice-chart.component';
import { ConvLeaksChartComponent } from './components/conversation-data/conv-leaks-chart/conv-leaks-chart.component';
import { ConvBlinksChartComponent } from './components/conversation-data/conv-blinks-chart/conv-blinks-chart.component';
import { ConvRgbChartComponent } from './components/conversation-data/conv-rgb-chart/conv-rgb-chart.component';
import { ConvEmotionChartComponent } from './components/conversation-data/conv-emotion-chart/conv-emotion-chart.component';
import { RecordConversationVideoComponent } from './components/record-conversation-video/record-conversation-video.component';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { ConvLeaksExtendedComponent } from './components/conversation-data/conv-leaks-extended/conv-leaks-extended.component';
import { ConvHnebChartComponent } from './components/conversation-data/conv-hneb-chart/conv-hneb-chart.component';
import { HeadTooltipTemplateComponent } from './components/conversation-data/conv-hneb-chart/head-tooltip-template/head-tooltip-template.component';
import { PasswordValidatorComponent } from './components/password-validator/password-validator.component';
import { CreateNewClientComponent } from './components/create-new-client/create-new-client.component';
import { ConvChatBotComponent } from './components/conversation-data/conv-chat-bot/conv-chat-bot.component';
import { ConvQHeatmapChartComponent } from './components/conversation-data/conv-q-heatmap-chart/conv-q-heatmap-chart.component';
import { ConvVertexaiChatComponent } from './components/conversation-data/conv-vertexai-chat/conv-vertexai-chat.component';
import { FormatAnswerPipe } from './pipes/format-answer.pipe';
import { FormatReportPipe } from './pipes/format-report.pipe';
import { ConvBlinksHeatmapComponent } from './components/conversation-data/conv-blinks-heatmap/conv-blinks-heatmap.component';
import { ConvSquintHeatmapComponent } from './components/conversation-data/conv-squint-heatmap/conv-squint-heatmap.component';
import { ResearchDataComponent } from './components/research-data/research-data.component';
import { ResEqualizerChartComponent } from './components/research-data/res-equalizer-chart/res-equalizer-chart.component';
import { ResPolarChartComponent } from './components/research-data/res-polar-chart/res-polar-chart.component';
import { ResHeatmapChartComponent } from './components/research-data/res-heatmap-chart/res-heatmap-chart.component';
import { ResVoiceChartComponent } from './components/research-data/res-voice-chart/res-voice-chart.component';
import { ResLeaksChartComponent } from './components/research-data/res-leaks-chart/res-leaks-chart.component';
import { ResBlinksChartComponent } from './components/research-data/res-blinks-chart/res-blinks-chart.component';
import { ResRgbChartComponent } from './components/research-data/res-rgb-chart/res-rgb-chart.component';
import { ResEmotionChartComponent } from './components/research-data/res-emotion-chart/res-emotion-chart.component';
import { ResLeaksExtendedComponent } from './components/research-data/res-leaks-extended/res-leaks-extended.component';
import { ResHnebChartComponent } from './components/research-data/res-hneb-chart/res-hneb-chart.component';
import { ResHeadTooltipTemplateComponent } from './components/research-data/res-hneb-chart/head-tooltip-template/head-tooltip-template.component';
import { ResChatBotComponent } from './components/research-data/res-chat-bot/res-chat-bot.component';
import { ResQHeatmapChartComponent } from './components/research-data/res-q-heatmap-chart/res-q-heatmap-chart.component';
import { ResVertexaiChatComponent } from './components/research-data/res-vertexai-chat/res-vertexai-chat.component';
import { ResBlinksHeatmapComponent } from './components/research-data/res-blinks-heatmap/res-blinks-heatmap.component';
import { ResSquintHeatmapComponent } from './components/research-data/res-squint-heatmap/res-squint-heatmap.component';
import { CreateAssessmentComponent } from './components/create-assessment/create-assessment.component';
import { UploadActivityVideoComponent } from './components/activities/upload-activity-video/upload-activity-video.component';
import { RecordActivitySessionComponent } from './components/activities/record-activity-session/record-activity-session.component';
import { ActivityConversationReportComponent } from './components/activities/activity-conversation-report/activity-conversation-report.component';
import { ReportGenerationComponent } from './components/report-generation/report-generation.component';
import { GenerateNewReportComponent } from './components/report-generation/generate-new-report/generate-new-report.component';
import { ViewGeneratedReportComponent } from './components/report-generation/view-generated-report/view-generated-report.component';
import { ViewAssessmentExamineeReportComponent } from './components/view-assessment-examinee-report/view-assessment-examinee-report.component';
import { CreateResearchProjectComponent } from './components/create-research-project/create-research-project.component';

export function createTranslateLoader(http: HttpClient): any {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
		if (params.interpolateParams) {
			const defaultTranslate: any = params.interpolateParams;
			return defaultTranslate['Default'];
		}

		return params.key;
  }
}

PlotlyModule.plotlyjs = PlotlyJS;

@NgModule({
  declarations: [
		SanitizeHtmlPipe,
		FormatReportPipe,
		FormatAnswerPipe,
		DateFormatterPipe,
		TruncatePipe,
		NgbdListSortableHeader,
		DndDirective,
		EulaAgreementComponent,
		CreateUserComponent,
  	LoadingSpinnerComponent,
		AssignProjectMemberComponent,
  	CreateProjectComponent,
   	QuestionnaireChaptersComponent,
    ChapterQuestionsComponent,
    ViewChapterQuestionComponent,
    InviteExamineeComponent,
    CreateConversationComponent,
    UploadConversationVideoComponent,
    ConversationDataComponent,
    ConvEqualizerChartComponent,
    ConvPolarChartComponent,
    ConvHeatmapChartComponent,
    ConvVoiceChartComponent,
    ConvLeaksChartComponent,
    ConvBlinksChartComponent,
    ConvRgbChartComponent,
    ConvEmotionChartComponent,
    RecordConversationVideoComponent,
    ConvLeaksExtendedComponent,
    ConvHnebChartComponent,
    HeadTooltipTemplateComponent,
    PasswordValidatorComponent,
    CreateNewClientComponent,
    ConvChatBotComponent,
    ConvQHeatmapChartComponent,
    ConvVertexaiChatComponent,
    ConvBlinksHeatmapComponent,
    ConvSquintHeatmapComponent,
    ResearchDataComponent,
    ResEqualizerChartComponent,
    ResPolarChartComponent,
    ResHeatmapChartComponent,
    ResVoiceChartComponent,
    ResLeaksChartComponent,
    ResBlinksChartComponent,
    ResRgbChartComponent,
    ResEmotionChartComponent,
    ResLeaksExtendedComponent,
    ResHnebChartComponent,
    ResHeadTooltipTemplateComponent,
    ResChatBotComponent,
    ResQHeatmapChartComponent,
    ResVertexaiChatComponent,
    ResBlinksHeatmapComponent,
    ResSquintHeatmapComponent,
    CreateAssessmentComponent,
    UploadActivityVideoComponent,
    RecordActivitySessionComponent,
    ActivityConversationReportComponent,
    ReportGenerationComponent,
    GenerateNewReportComponent,
    ViewGeneratedReportComponent,
    ViewAssessmentExamineeReportComponent,
    CreateResearchProjectComponent
  ],
  imports: [
    CommonModule,
		NgbModule,
		NgbNavModule,
		FormsModule,
		ReactiveFormsModule,
		NgSelectModule,
		SimplebarAngularModule,
		NgxChartsModule,
		PlotlyModule,
		FilePondModule,
		NgCircleProgressModule.forRoot(),
		QuillModule.forRoot(),
		FlatpickrModule.forRoot(),
		TranslateModule.forRoot({
      defaultLanguage: 'en',
			missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler },
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
		QuillEditorComponent,
		NgbScrollSpyModule
  ],
	exports: [
		SanitizeHtmlPipe,
		DateFormatterPipe,
		TruncatePipe,
		NgbModule,
		NgbNavModule,
		FormsModule,
		ReactiveFormsModule,
		NgbdListSortableHeader,
		DndDirective,
		NgSelectModule,
		TranslateModule,
		FilePondModule,
		SimplebarAngularModule,
		NgxChartsModule,
		PlotlyModule,
		EulaAgreementComponent,
		CreateUserComponent,
		CreateConversationComponent,
		LoadingSpinnerComponent,
		AssignProjectMemberComponent,
		QuestionnaireChaptersComponent,
		CreateProjectComponent,
		QuillEditorComponent,
		QuillModule,
		NgCircleProgressModule,
		ChapterQuestionsComponent,
		ViewChapterQuestionComponent,
		UploadConversationVideoComponent,
		InviteExamineeComponent,
		ConversationDataComponent,
		ResearchDataComponent,
		RecordConversationVideoComponent,
		NgbScrollSpyModule,
		PasswordValidatorComponent,
		CreateNewClientComponent,
		CreateAssessmentComponent,
		UploadActivityVideoComponent,
		RecordActivitySessionComponent,
		ActivityConversationReportComponent,
		ReportGenerationComponent,
		GenerateNewReportComponent,
		ViewAssessmentExamineeReportComponent,
    CreateResearchProjectComponent
  ],
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule {
	constructor() {
    defineElement(lottie.loadAnimation);
  }
}
